import type React from 'react';
import styled from 'styled-components';

import Timer from 'app/ui/components/molecules/timer';
import Typography from 'app/ui/components/atoms/typography';

export interface SlideTimerProps {
  isPresenterView?: boolean;
  countdownTime: number;
  isWorkTime: boolean;
  currentRound?: number;
  totalRounds?: number;
  currentSection?: 'Work' | 'Rest';
  hasWorkRestSections?: boolean;
  progressPercentage: number;
  onReset: () => void;
}

const TimerContainer = styled.div<{ $isPresenterView?: boolean; isWorkTime?: boolean; percentage?: number }>`
  position: ${({ $isPresenterView }) => ($isPresenterView ? 'block' : 'absolute')};
  top: 2rem;
  right: 2rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  border: ${({ theme, isWorkTime }) => `4px solid ${isWorkTime ? theme.colors.timerGreen : theme.colors.firebreathingRed}`};
  border-radius: ${({ theme }) => theme.spacing(3)};
  height: 6.5rem;
  width: 500px;
  ${({ theme: { media } }) => `
    ${media.xl(
      () => `
        width: 36vw;
      `,
    )}
  `}
`;

const TopTimer = styled(Timer).attrs<{ isWorkTime: boolean }>(({ isWorkTime }) => ({
  isWorkTime,
}))`
  display: inline-flex;
  border: none;
  background: ${({ theme }) => theme.colors.black};
  border-radius: 0;
  border-top-left-radius: ${({ theme }) => theme.spacing(3)};
  border-bottom-left-radius: ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.colors.white};
  height: 100%;
  width: 16.5rem;
  font-size: 4.75rem;
`;

const TextPeriodWrapper = styled(Typography)`
  width: 14rem;
  display: flex;
  justify-content: center;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};
  font-weight: 600;
  letter-spacing: -2%;
  position: relative;
  z-index: 1;
`;

const SectionWrapper = styled.div<{ isWorkTime: boolean; percentage: number }>`
  height: 100%;
  width: 100%;
  z-index: -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${({ theme, isWorkTime }) => (isWorkTime ? theme.colors.greenWithLessTransparency : theme.colors.redWithLessTransparency)};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.black};
    transform: ${({ percentage }) => `translateX(${percentage}%)`};
    transition: transform 1.1s ease-in-out;
  }
`;

const SlideTimer: React.FC<SlideTimerProps> = ({
  isPresenterView = false,
  countdownTime,
  isWorkTime,
  currentRound,
  totalRounds,
  currentSection,
  hasWorkRestSections = false,
  progressPercentage,
  onReset,
}) => {
  return !hasWorkRestSections ? (
    <TimerContainer data-testid="top-timer" isWorkTime={isWorkTime}>
      <TopTimer isPresenterView={isPresenterView} elapsedTime={countdownTime} reset={onReset} isWorkTime={isWorkTime} />
      <SectionWrapper isWorkTime={isWorkTime} percentage={progressPercentage}>
        <TextPeriodWrapper variant="h2">Work</TextPeriodWrapper>
      </SectionWrapper>
    </TimerContainer>
  ) : (
    <TimerContainer data-testid="top-timer" isWorkTime={isWorkTime} percentage={progressPercentage}>
      <TopTimer isPresenterView={isPresenterView} elapsedTime={countdownTime} isWorkTime={isWorkTime} reset={onReset} />
      <SectionWrapper key={`${currentSection}-${currentRound}`} isWorkTime={isWorkTime} percentage={progressPercentage}>
        <TextPeriodWrapper variant="h3">
          {hasWorkRestSections && `${currentRound} / ${totalRounds} - `}
          {currentSection}
        </TextPeriodWrapper>
      </SectionWrapper>
    </TimerContainer>
  );
};

export default SlideTimer;
